'use client';

import * as React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { EVENT_COLORS } from '@mui/x-scheduler-headless/constants';
import { useSchedulerStoreContext } from '@mui/x-scheduler-headless/use-scheduler-store-context';
import { schedulerOtherSelectors, schedulerResourceSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import { useStore } from '@base-ui/utils/store';
import { getPaletteVariants } from "../../utils/tokens.mjs";
import { useEventDialogStyledContext } from "./EventDialogStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const NO_RESOURCE_VALUE = '';
const ResourceMenuItem = styled(MenuItem, {
  name: 'MuiEventDialog',
  slot: 'ResourceMenuItem'
})(({
  theme
}) => ({
  paddingLeft: `calc(${theme.spacing(2)} + var(--resource-indent) * ${theme.spacing(2)})`
}));
const ResourceMenuListSubheader = styled(ListSubheader, {
  name: 'MuiEventDialog',
  slot: 'ResourceMenuListSubheader'
})({});
const ResourceMenuColorDot = styled('span', {
  name: 'MuiEventDialog',
  slot: 'ResourceMenuColorDot'
})(({
  theme
}) => ({
  width: 14,
  height: 14,
  borderRadius: '2px',
  flexShrink: 0,
  backgroundColor: 'var(--event-main)',
  variants: getPaletteVariants(theme),
  [`&[data-no-resource="true"]`]: {
    backgroundColor: 'var(--event-surface-subtle)',
    border: '1.2px dashed var(--event-main)'
  }
}));
const ColorSelectionContainer = styled('div', {
  name: 'MuiEventDialog',
  slot: 'ColorSelectionContainer'
})(({
  theme
}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius
}));
const ResourceMenuColorRadioButton = styled('button', {
  name: 'MuiEventDialog',
  slot: 'ResourceMenuColorRadioButton'
})(({
  theme
}) => ({
  width: 24,
  height: 24,
  borderRadius: theme.shape.borderRadius,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--event-main)',
  color: 'white',
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5
  },
  variants: getPaletteVariants(theme)
}));
function ResourceSelectAdornment(props) {
  const {
    resource
  } = props;
  const store = useSchedulerStoreContext();
  const {
    classes
  } = useEventDialogStyledContext();
  const resourceColor = useStore(store, schedulerResourceSelectors.defaultEventColor, resource?.value);
  return /*#__PURE__*/_jsx(ResourceMenuColorDot, {
    className: classes.eventDialogResourceMenuColorDot,
    "data-palette": resourceColor,
    "data-no-resource": Boolean(resource?.value === null)
  });
}
export default function ResourceAndColorSection(props) {
  const {
    readOnly,
    resourceId,
    onResourceChange,
    onColorChange,
    color
  } = props;

  // Context hooks
  const {
    classes,
    localeText
  } = useEventDialogStyledContext();
  const store = useSchedulerStoreContext();

  // Selector hooks
  const resources = useStore(store, schedulerResourceSelectors.processedResourceFlatList);
  const resourceDepthLookup = useStore(store, schedulerResourceSelectors.resourceDepthLookup);
  const childrenIdLookup = useStore(store, schedulerResourceSelectors.childrenIdLookup);
  const eventDefaultColor = useStore(store, schedulerOtherSelectors.defaultEventColor);
  const resourcesOptions = React.useMemo(() => {
    const hasNesting = resources.some(resource => (childrenIdLookup.get(resource.id)?.length ?? 0) > 0);
    return [{
      label: localeText.labelNoResource,
      value: null,
      eventColor: eventDefaultColor,
      isGroupRoot: false,
      indentLevel: 0,
      showDivider: false
    }, ...resources.map(resource => {
      const depth = resourceDepthLookup.get(resource.id) ?? 0;
      const hasChildren = (childrenIdLookup.get(resource.id)?.length ?? 0) > 0;
      return {
        label: resource.title,
        value: resource.id,
        eventColor: resource.eventColor ?? eventDefaultColor,
        isGroupRoot: depth === 0 && hasChildren,
        indentLevel: Math.max(0, depth - 1),
        showDivider: hasNesting && depth === 0
      };
    })];
  }, [resources, resourceDepthLookup, childrenIdLookup, localeText.labelNoResource, eventDefaultColor]);
  const resource = React.useMemo(() => resourcesOptions.find(option => resourceId ? option.value === resourceId : option.value === null) || null, [resourcesOptions, resourceId]);
  const handleChange = event => {
    const value = event.target.value;
    onResourceChange(value === NO_RESOURCE_VALUE ? null : value);
  };
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [/*#__PURE__*/_jsxs(FormControl, {
      size: "small",
      fullWidth: true,
      children: [/*#__PURE__*/_jsx(InputLabel, {
        id: "resource-select-label",
        children: localeText.resourceLabel
      }), /*#__PURE__*/_jsx(Select, {
        labelId: "resource-select-label",
        label: localeText.resourceLabel,
        value: resourceId ?? NO_RESOURCE_VALUE,
        displayEmpty: true,
        onChange: handleChange,
        readOnly: readOnly,
        startAdornment: /*#__PURE__*/_jsx(InputAdornment, {
          position: "start",
          children: /*#__PURE__*/_jsx(ResourceSelectAdornment, {
            resource: resource
          })
        }),
        renderValue: () => resource ? resource.label : localeText.labelInvalidResource,
        children: resourcesOptions.flatMap(resourceOption => {
          const items = [];
          if (resourceOption.showDivider) {
            items.push(/*#__PURE__*/_jsx(Divider, {}, `divider-${resourceOption.value}`));
          }
          if (resourceOption.isGroupRoot) {
            items.push(/*#__PURE__*/_jsx(ResourceMenuListSubheader, {
              className: classes.eventDialogResourceMenuListSubheader,
              children: resourceOption.label.toUpperCase()
            }, `header-${resourceOption.value}`));
          }
          items.push(/*#__PURE__*/_jsxs(ResourceMenuItem, {
            value: resourceOption.value ?? NO_RESOURCE_VALUE,
            "aria-label": resourceOption.label,
            className: classes.eventDialogResourceMenuItem,
            style: {
              '--resource-indent': resourceOption.indentLevel
            },
            children: [/*#__PURE__*/_jsx(ListItemIcon, {
              children: /*#__PURE__*/_jsx(ResourceMenuColorDot, {
                className: classes.eventDialogResourceMenuColorDot,
                "data-palette": resourceOption.eventColor,
                "data-no-resource": Boolean(resourceOption.value === null)
              })
            }), /*#__PURE__*/_jsx(ListItemText, {
              children: resourceOption.label
            })]
          }, resourceOption.value ?? NO_RESOURCE_VALUE));
          return items;
        })
      })]
    }), /*#__PURE__*/_jsx(ColorSelectionContainer, {
      role: "radiogroup",
      "aria-label": localeText.colorPickerLabel,
      children: EVENT_COLORS.map(colorOption => /*#__PURE__*/_jsx(ResourceMenuColorRadioButton, {
        type: "button",
        role: "radio",
        "aria-checked": color === colorOption,
        disabled: readOnly,
        onClick: () => onColorChange(colorOption),
        "aria-label": `Select ${colorOption} as event color`,
        "data-palette": colorOption,
        className: classes.eventDialogResourceMenuColorRadioButton,
        children: color === colorOption && /*#__PURE__*/_jsx(CheckIcon, {
          fontSize: "small"
        })
      }, colorOption))
    })]
  });
}